import { useMemo, useState } from 'react';
import { Download, PhoneOutgoing, Printer, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/ui/Loader';
import { useModuleAccess } from '@/lib/useModuleAccess';
import {
  buildOutboundRoutingReport,
  downloadHtml,
  openPrintableHtml,
  renderOutboundRoutingHtml,
  type OutboundPermissionEntry,
  type OutboundRoutingReport as ReportData,
} from '@/services/configReports';

function KindTag({ kind }: { kind: OutboundPermissionEntry['kind'] }) {
  const styles: Record<OutboundPermissionEntry['kind'], string> = {
    ext_group: 'bg-teal-700',
    extension: 'bg-violet-700',
    organization: 'bg-amber-700',
    other: 'bg-slate-600',
  };
  const labels: Record<OutboundPermissionEntry['kind'], string> = {
    ext_group: 'EXT GROUP',
    extension: 'EXT',
    organization: 'ORG',
    other: 'OTHER',
  };
  return (
    <span
      className={`inline-block text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded mr-1.5 text-white ${styles[kind]}`}
    >
      {labels[kind]}
    </span>
  );
}

function PermissionBlock({ entry }: { entry: OutboundPermissionEntry }) {
  // Member names are shown as pills in "All extensions that can dial out" —
  // avoid duplicating a long comma-separated list under groups.
  const showUnresolved =
    entry.kind !== 'extension' &&
    entry.members.length === 0 &&
    Boolean(entry.membersUnresolved);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <KindTag kind={entry.kind} />
        <span className="font-semibold text-sm text-gray-900 dark:text-white">
          {entry.label}
        </span>
        {entry.kind !== 'extension' && (
          <span className="text-[11px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded-full">
            {entry.memberCount} member{entry.memberCount === 1 ? '' : 's'}
          </span>
        )}
      </div>
      {showUnresolved && (
        <div className="mt-2 text-xs">
          <span className="text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded inline-block">
            Members not available from PBX API
          </span>
        </div>
      )}
    </div>
  );
}

export function OutboundRoutingReport() {
  const { session } = useModuleAccess('reporting');
  const customerName = session?.customer?.name || 'Customer';
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState<ReportData | null>(null);

  const generate = async () => {
    setLoading(true);
    try {
      const data = await buildOutboundRoutingReport(customerName);
      setReport(data);
      toast.success(`Loaded ${data.cards.length} outbound route(s)`);
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to build outbound routing report'
      );
    } finally {
      setLoading(false);
    }
  };

  const html = useMemo(
    () => (report ? renderOutboundRoutingHtml(report) : ''),
    [report]
  );

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-teal-100 dark:bg-teal-900/30 rounded-xl">
              <PhoneOutgoing className="w-6 h-6 text-teal-700 dark:text-teal-300" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Outbound Routing Report
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Live outbound routes with dial patterns, trunks, and which extensions
                can dial out for {customerName}&apos;s PBX.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void generate()} isLoading={loading} size="sm">
              <RefreshCw className="w-4 h-4 mr-1.5" />
              {report ? 'Refresh' : 'Generate'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!report}
              onClick={() => {
                if (!report) return;
                downloadHtml(
                  `Outbound_Routing_${customerName.replace(/\s+/g, '_')}.html`,
                  html
                );
                toast.success('HTML downloaded');
              }}
            >
              <Download className="w-4 h-4 mr-1.5" />
              Download HTML
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={!report}
              onClick={() => {
                if (!html) return;
                openPrintableHtml(html);
              }}
            >
              <Printer className="w-4 h-4 mr-1.5" />
              Print / PDF
            </Button>
          </div>
        </div>
      </Card>

      {loading && (
        <Card>
          <Loader text="Loading outbound routes, extension groups, and members from PBX..." />
        </Card>
      )}

      {!loading && report && (
        <>
          <Card>
            <div className="flex flex-wrap gap-4 text-xs text-gray-600 dark:text-gray-300">
              <span className="inline-flex items-center gap-1.5">
                <i className="w-3 h-3 rounded-sm bg-teal-700 inline-block" /> Extension group
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="w-3 h-3 rounded-sm bg-violet-700 inline-block" /> Individual
                extension
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="w-3 h-3 rounded-sm bg-amber-700 inline-block" /> Organization
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="w-3 h-3 rounded-sm bg-emerald-700 inline-block" /> Dial pattern
              </span>
            </div>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Generated {new Date(report.generatedAt).toLocaleString()} · {report.totalRoutes}{' '}
              route(s) · {report.totalUniqueDialers} unique dialer(s) · each route hydrated
              via outbound_route/get · extension groups expanded via extension_group/query
            </p>
          </Card>

          {report.cards.length === 0 ? (
            <Card>
              <p className="text-center text-gray-500 dark:text-gray-400 py-10">
                No outbound routes found on this PBX.
              </p>
            </Card>
          ) : (
            report.cards.map((card) => (
              <Card key={card.id} className="overflow-hidden p-0">
                <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-3">
                  <span className="text-xs font-bold text-white bg-teal-600 px-2 py-0.5 rounded">
                    OUTBOUND
                  </span>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex-1">
                    {card.name}
                  </h4>
                  {card.pos != null && (
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      Priority {card.pos}
                    </span>
                  )}
                </div>
                <div className="px-5 py-2.5 bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-600 dark:text-gray-300 flex flex-wrap gap-x-5 gap-y-1 border-b border-gray-200 dark:border-gray-700">
                  <span>
                    Trunks:{' '}
                    <b className="text-gray-900 dark:text-white">{card.trunksLabel}</b>
                  </span>
                  <span>
                    Outbound CID:{' '}
                    <b className="text-gray-900 dark:text-white">{card.outboundCidLabel}</b>
                  </span>
                  <span>
                    PIN: <b className="text-gray-900 dark:text-white">{card.pinLabel}</b>
                  </span>
                  <span>
                    Dialers:{' '}
                    <b className="text-gray-900 dark:text-white">{card.dialerCount}</b>
                  </span>
                </div>

                <div className="p-5 space-y-5">
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Dial patterns
                    </div>
                    {card.dialPatterns.length === 0 ? (
                      <p className="text-sm text-gray-400 italic">No dial patterns</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {card.dialPatterns.map((p) => (
                          <span
                            key={p.label}
                            className="inline-block rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-900 dark:text-emerald-200 text-sm font-semibold px-3 py-1.5 tabular-nums"
                          >
                            {p.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                      Who can dial out
                    </div>
                    {card.permissions.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                        No extensions or groups selected — nobody can dial out on this route.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {card.permissions.map((entry, idx) => (
                          <PermissionBlock
                            key={`${entry.kind}-${entry.id ?? entry.label}-${idx}`}
                            entry={entry}
                          />
                        ))}
                      </div>
                    )}
                  </div>

                  {card.allDialers.length > 0 && (
                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                        All extensions that can dial out ({card.dialerCount})
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {card.allDialers.map((d) => (
                          <span
                            key={d.number}
                            className="inline-flex items-center gap-1 rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-2 py-1 text-xs"
                          >
                            <b className="text-violet-700 dark:text-violet-300 tabular-nums">
                              {d.number}
                            </b>
                            {d.name && d.name !== '—' ? (
                              <span className="text-gray-700 dark:text-gray-300">{d.name}</span>
                            ) : null}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            ))
          )}
        </>
      )}

      {!loading && !report && (
        <Card>
          <p className="text-center text-gray-500 dark:text-gray-400 py-10">
            Click <strong>Generate</strong> to pull the live outbound routing configuration
            from the PBX. Extension groups configured on a route are expanded to show all
            member extensions.
          </p>
        </Card>
      )}
    </div>
  );
}
